/**
 * Кросс-вкладочная синхронизация состояния анкеты через BroadcastChannel.
 *
 * Когда в одной вкладке происходит значимое изменение (смена шага, авторизация
 * через Сбер ID, смена/сброс/генерация clientId), остальные вкладки получают
 * сигнал "sync" и перезагружаются — так они гарантированно подтягивают
 * актуальное состояние, без ручной пересборки стора.
 *
 * Отдельно есть сигнал "finished": анкета была успешно отправлена в одной из
 * вкладок. Остальные вкладки в этом случае не перезагружаются сами (чтобы не
 * потерять локальный контекст поверх уже очищенного localStorage), а лишь
 * предупреждают пользователя, что анкета уже отправлена и страницу нужно
 * обновить вручную.
 *
 * Сигнал "outdated": вкладка обнаружила устаревшую версию SPA
 * или backend (см. src/containers/version-check). Остальные вкладки не
 * перезагружаются сами, а сразу показывают блокирующую модалку с просьбой
 * обновить страницу, не дожидаясь своего тика поллинга.
 */
const CHANNEL_NAME = "questionnaire-sync";

export type CrossTabMessage = "sync" | "finished" | "outdated";

const KNOWN_MESSAGES: readonly CrossTabMessage[] = ["sync", "finished", "outdated"];

const isCrossTabMessage = (data: unknown): data is CrossTabMessage =>
  typeof data === "string" && (KNOWN_MESSAGES as readonly string[]).includes(data);

let channel: BroadcastChannel | null = null;
const listeners = new Set<(message: CrossTabMessage) => void>();

const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      if (!isCrossTabMessage(event.data)) return;
      listeners.forEach((listener) => listener(event.data));
    };
  }
  return channel;
};

export const postCrossTabSync = (message: CrossTabMessage = "sync"): void => {
  try {
    getChannel()?.postMessage(message);
  } catch {
  }
};

export const subscribeCrossTab = (listener: (message: CrossTabMessage) => void): (() => void) => {
  getChannel();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
